/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { DataType, toggleClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import {
  setCommonCodeGroups,
  getCommonCodeGroups,
  getCommonCodeNamesCondition,
  getCommonCodes,
  setCommonCodes,
} from 'apis/admin/CommonCode';
import CustomGrid from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { CommonCode, CommonCodeGroup } from 'models/admin/CommonCode';
import { CommonYN } from 'models/common/Common';
import { commonYNList } from 'models/common/CommonCode';
import { CrudCode } from 'models/common/Edit';
import { useMessageModalStore } from 'stores/useMessageModalStore';
import { getExcelFileName } from 'utils/ExcelUtil';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { Button, TableCell } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import _ from 'lodash';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { ComboBox } from 'components/selects/ComboBox';
import { SearchCols } from 'components/layouts/SearchBox';

interface Props {
  hasAuth?: boolean;
  gridRefresh?: boolean;
}

const MpCodeLv3Grid = ({ hasAuth, gridRefresh }: Props) => {
  const { t } = useTranslation();
  const gridRef = useRef<any>();
  const [flexRef, setflexRef] = useState<any>();
  const [rowData, setRowData] = useState<CommonCode[]>([]);
  const [mpImprTypeLv1Code, setMpImprTypeLv1Code] = useState<CommonCode[]>([]);
  const [mpImprTypeLv2Code, setMpImprTypeLv2Code] = useState<CommonCode[]>([]);
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const { setMessageModalStateWhenModalOpen } = useMessageModalStore();

  //LV3 행추가 콤보박스 용도
  const [mpImprTypeLv1Value, setMpImprTypeLv1Value] = useState<string>('');
  const [mpImprTypeLv2Value, setMpImprTypeLv2Value] = useState<string>('');
  const [mpImprTypeLv2Options, setMpImprTypeLv2Options] = useState<CommonCode[]>([]);

  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    const mpImprTypeLv2Option = mpImprTypeLv2Code.filter(
      (value) => value.optValCtn1 === mpImprTypeLv1Value
    );
    setMpImprTypeLv2Options(mpImprTypeLv2Option);
  }, [mpImprTypeLv1Value]);

  const ecncCode = [
    { cmnCd: 'EC', cmnCdNm: 'EC' },
    { cmnCd: 'NC', cmnCdNm: 'NC' },
  ];

  useEffect(() => {
    getCommonCode();
  }, [gridRefresh]);

  const getCommonCode = async () => {
    const mpImprTypeLv1: CommonCode[] = await getCommonCodes('MP_IMPR_TYPE_LVL1'); // MP 분류
    const mpImprTypeLv2: CommonCode[] = await getCommonCodes('MP_IMPR_TYPE_LVL2'); // MP 분류
    const mpImprTypeLv3: CommonCode[] = await getCommonCodes('MP_IMPR_TYPE_LVL3'); // MP 분류
    setRowData(mpImprTypeLv3);
    setMpImprTypeLv1Code(mpImprTypeLv1);
    setMpImprTypeLv2Code(mpImprTypeLv2);
  };

  const layoutDefinition = useMemo(() => {
    const mpImprTypeLv1DataMap = new DataMap(mpImprTypeLv1Code, 'cmnCd', 'cmnCdNm');
    const mpImprTypeLv2DataMap = new DataMap(mpImprTypeLv2Code, 'cmnCd', 'cmnCdNm');
    const ecncDataMap = new DataMap(ecncCode, 'cmnCd', 'cmnCdNm');

    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        width: 45,
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'optValCtn1',
        header: String(t('com.label.개선목적Lv1', '개선목적Lv1')),
        width: 230,
        cssClass: 'WijmoSelect',
        align: 'center',
        dataMap: mpImprTypeLv1DataMap,
      },
      {
        header: String(t('admin.grid.개선목적Lv2', '개선목적Lv2')),
        binding: 'optValCtn2',
        width: 230,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: mpImprTypeLv2DataMap,
        cellTemplate: (item) => {
          if (item.item.crudKey !== null) {
            return `<span>${
              item.item.optValCtn2 === undefined
                ? ''
                : mpImprTypeLv2DataMap.getDisplayValue(item.item.optValCtn2)
            }</span>`;
          } else {
            return `<span>${mpImprTypeLv2DataMap.getDisplayValue(item.item.optValCtn2)}</span>`;
          }
        },
      },
      {
        header: String(t('admin.grid.코드', '코드')),
        binding: 'cmnCd',
        width: 180,
        align: 'center',
        isReadOnly: true,
      },
      {
        header: String(t('admin.grid.개선목적명', '개선목적명')),
        binding: 'cmnCdNm',
        align: 'left',
        minWidth: 150,
        width: '*',
        cssClass: 'WijmoPopup',
        isReadOnly: !hasAuth,
        cellTemplate: (params) =>
          params.item['crudKey'] === CrudCode.CREATE
            ? params.value
            : `<span>${params.value}</span><Button id="${hasAuth ? 'btnCode' : ''}" />`,
      },
      {
        header: String(t('admin.grid.EC/NC', 'EC/NC')),
        binding: 'optValCtn3',
        width: 100,
        align: 'center',
        cssClass: 'WijmoSelect',
        dataMap: ecncDataMap,
        isReadOnly: !hasAuth,
      },
      {
        header: String(t('admin.grid.순서', '순서')),
        binding: 'sortOrd',
        width: 100,
        align: 'center',
        isReadOnly: !hasAuth,
      },
      {
        header: String(t('com.label.사용여부', '사용여부')),
        binding: 'useYn',
        width: 120,
        align: 'center',
        dataMap: new DataMap(commonYNList, 'cmnCd', 'cmnCdNm'),
        isReadOnly: !hasAuth,
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        width: 120,
        align: 'center',
        isReadOnly: true,
      },
    ];
  }, [rowData]);

  const btnExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = '개선목적Lv3';
    book.saveAsync(getExcelFileName(t('admin.label.MP개선목적Lv3', 'MP개선목적Lv3')));
  };

  const beginningEdit = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;

    if (item.crudKey !== CrudCode.CREATE) {
      if (['optValCtn1', 'optValCtn2', 'cmnCd'].includes(binding)) {
        e.cancel = true;
      }
    }
    if (binding === 'optValCtn1' && item.crudKey === CrudCode.CREATE) {
      item.optValCtn2 = '';
      item.cmnCd = '';
    }
    if (binding === 'optValCtn2' && item.crudKey === CrudCode.CREATE) {
      const mpImprTypeLv2 = mpImprTypeLv2Code.filter(
        (value) => value.optValCtn1 === item.optValCtn1
      );
      col.dataMap = new DataMap(mpImprTypeLv2, 'cmnCd', 'cmnCdNm');
    }
  });

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (item.crudKey !== CrudCode.CREATE) {
        if (['optValCtn1', 'optValCtn2', 'cmnCd'].includes(binding)) {
          cell.ariaReadOnly = true;
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const cellEditEnded = useEvent((grid, e) => {
    const col = grid.columns[e.col];
    const binding = col.binding;
    const item = grid.rows[e.row].dataItem;
    const selectLv2cmnCd = rowData.filter((data) => data.optValCtn2 === item.optValCtn2);
    if (item.crudKey === CrudCode.CREATE) {
      if (binding === 'optValCtn2') {
        item.cmnCd = '';
        const cmnCd = selectLv2cmnCd
          .map((data) => Number(data.cmnCd?.match(/\d+$/)?.at(0)))
          .filter((data) => data !== undefined && data !== null && !Number.isNaN(data))
          .sort((a, b) => a - b);

        let index = 0;
        const findCmnCdNumber = cmnCd.find((currentValue, i) => {
          const missingNumber = currentValue !== i + 1;
          if (missingNumber) {
            index = i + 1;
          }
          return missingNumber;
        });

        const maxCmnCd = findCmnCdNumber ? String(index) : String(Number(cmnCd.pop()) + Number(1));

        item.cmnCd = item.optValCtn2 ? item.optValCtn2 + '-' + maxCmnCd : '';
        gridRef.current.refresh();
      }
      if (binding === 'optValCtn1') {
        item.cmnCd = '';
        gridRef.current.refresh();
      }
    }
  });

  const onInitialized = (grid) => {
    setflexRef(grid);
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.id) {
          // 그룹코드
          case 'btnCode':
            setMessageModalStateWhenModalOpen(item.msgCtn, item.cmnCdNm, (cmnCdNm?: string) => {
              if (cmnCdNm && item) {
                setCommonCodeGroups([{ ...item, cmnCdNm: cmnCdNm, crudKey: CrudCode.UPDATE }]).then(
                  (result) => result && result && getCommonCode()
                );
              }
              openMessageBar({
                type: 'confirm',
                content: t('com.label.저장되었습니다.', '저장되었습니다.'),
              });
            });
        }
      }
    });
  };

  const btnAddRow = () => {
    if (_.isEmpty(mpImprTypeLv1Value)) {
      setErrors({ mpImprTypeLv1Value: true });
      openMessageBar({
        type: 'error',
        content: t('com.label.개선목적LV1을 선택해 주세요.', '개선목적LV1을 선택해 주세요.'),
      });
      return;
    }
    if (_.isEmpty(mpImprTypeLv2Value)) {
      setErrors({ mpImprTypeLv2Value: true });
      openMessageBar({
        type: 'error',
        content: t('com.label.개선목적LV2를 선택해 주세요.', '개선목적LV2를 선택해 주세요.'),
      });
      return;
    }

    const selectLv2cmnCd = rowData.filter((data) => data?.optValCtn2 === mpImprTypeLv2Value);

    const cmnCd = selectLv2cmnCd
      .map((data) => {
        const match = data.cmnCd?.match(/\d+$/);
        return Number(match ? match[0] : 0);
      })
      .filter((data) => data !== undefined && data !== null && !Number.isNaN(data))
      .sort((a, b) => a - b);
    let index = 0;
    const findCmnCdNumber = cmnCd.find((currentValue, i) => {
      const missingNumber = currentValue !== i + 1;
      if (missingNumber) {
        index = i + 1;
      }
      return missingNumber;
    });

    const maxCmnCd = findCmnCdNumber ? String(index) : String(Number(cmnCd.pop()) + Number(1));

    const newRow = {
      crudKey: CrudCode.CREATE,
      cmnGrCd: 'MP_IMPR_TYPE_LVL3',
      cmnCd: mpImprTypeLv2Value + '-' + maxCmnCd,
      optValCtn1: mpImprTypeLv1Value || '',
      optValCtn2: mpImprTypeLv2Value || '',
      optValCtn3: 'EC',
      useYn: CommonYN.Y,
    } as CommonCode;

    setRowData([newRow, ...rowData]);
    gridRef.current.refresh();
  };

  const btnDelRow = useCallback(() => {
    const isSelected = gridRef.current.rows.filter((item) => item.isSelected);

    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('mp.label.삭제할 행을 선택해 주세요.', '삭제할 행을 선택해 주세요.'),
      });
      return false;
    } else {
      if (isSelected.length < 1) return;
      isSelected.forEach((row) => {
        const item = row.dataItem;
        if (item.crudKey === CrudCode.CREATE) {
          gridRef.current.collectionView.remove(item);
        } else {
          item.crudKey = CrudCode.UPDATE;
          item.useYn = 'N';
        }
      });
      gridRef.current.refresh();
    }
  }, [rowData]);

  const btnSave = () => {
    const valid = rowData
      .map((rowNode, index) => {
        if (rowNode.optValCtn1 == null || rowNode.optValCtn1 == '')
          return `${index + 1} : ${t(
            'admin.msg.개선목적Lv1을 선택해 주세요.',
            '개선목적Lv1을 선택해 주세요.'
          )}`;
        if (rowNode.optValCtn2 == null || rowNode.optValCtn2 == '')
          return `${index + 1} : ${t(
            'admin.msg.개선목적Lv2를 선택해 주세요.',
            '개선목적Lv2를 선택해 주세요.'
          )}`;
        if (rowNode.cmnCd == null || rowNode.cmnCd == '')
          return `${index + 1} : ${t('admin.msg.코드를 입력해 주세요.', '코드를 입력해 주세요.')}`;
        if (rowNode.cmnCdNm == null || rowNode.cmnCdNm == '')
          return `${index + 1} : ${t(
            'admin.msg.코드명을 입력해 주세요.',
            '코드명을 입력해 주세요.'
          )}`;
        if (rowNode.sortOrd == null || rowNode.sortOrd == '') rowNode.sortOrd = '0';
      })
      .filter((element) => element !== undefined);
    if (valid?.length) {
      openMessageBar({ type: 'error', content: valid.toString() });
      return false;
    }

    const saveRows = rowData
      .map((rowNode) => {
        return rowNode.crudKey ? rowNode : null;
      })
      .filter((element) => element !== null) as CommonCode[];

    if (saveRows?.length == 0) {
      openMessageBar({
        type: 'error',
        content: t('com.label.수정된 항목이 없습니다.', '수정된 항목이 없습니다.'),
      });
      return true;
    }
    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        setCommonCodes(saveRows).then((result) => {
          if (result.data.type === 'error') {
            openMessageBar({
              type: 'error',
              content:
                'Code: ' +
                result.data.code +
                ' ' +
                t('com.label.중복된 코드가 존재합니다.', '중복된 코드가 존재합니다.'),
            });
            return false;
          }
          if (result.data.type === 'used') {
            openMessageBar({
              type: 'error',
              content:
                'Code: ' +
                result.data.code +
                ' ' +
                t(
                  'com.label.사용중인 코드입니다. 삭제할 수 없습니다.',
                  '사용중인 코드입니다. 삭제할 수 없습니다.'
                ),
            });
            return false;
          }
          if (result.data.type === 'confirm') {
            openMessageBar({
              type: 'confirm',
              content: t('com.label.저장되었습니다.', '저장되었습니다.'),
            });
          }
          gridRef.current.refresh();
          getCommonCode();
          // fnSearchCodeGroup(props.sGrCd || '', props.sUseYn || '', props.sCode || '');
        });
      },
      noCallback: () => {
        return false;
      },
    });
  };

  // const onCellClicked = (params: any) => {
  //   props.callBack && params.crudKey != CrudCode.CREATE && props.callBack(params.cmnGrCd);
  // };

  // useEffect(() => {
  //   if (props.searchParamData != false) {
  //     fnSearchCodeGroup(props.sGrCd || '', props.sUseYn || '', props.sCode || '');
  //   }
  // }, [props.sGrCd, props.sUseYn, props.sCode, props.searchParamData, props.searchState]);

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('admin.label.개선목적 Level3', '개선목적 Level3')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{rowData?.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        {hasAuth && (
          <ControlBtnGroup>
            <label>
              <span className="dot">{t('com.label.개선목적', '개선목적')}</span>
            </label>
            <ComboBox
              options={mpImprTypeLv1Code}
              placeholder={String(t('com.label.선택', '선택'))}
              defaultValue={mpImprTypeLv1Value}
              onChange={(value) => {
                setMpImprTypeLv1Value(value);
                setMpImprTypeLv2Value('');
                setErrors({ mpImprTypeLv1Value: false });
              }}
              isError={errors?.mpImprTypeLv1Value}
              msgError={String(
                t('mp.msg.개선목적Lv1을 선택해 주세요.', '개선목적Lv1을 선택해 주세요.')
              )}
            />
            <ComboBox
              options={mpImprTypeLv2Options}
              placeholder={String(t('com.label.선택', '선택'))}
              defaultValue={mpImprTypeLv2Value}
              onChange={(value) => {
                setMpImprTypeLv2Value(value);
                setErrors({ mpImprTypeLv2Value: false });
              }}
              isError={errors?.mpImprTypeLv2Value}
              msgError={String(
                t('mp.msg.개선목적Lv2를 선택해 주세요.', '개선목적Lv2를 선택해 주세요.')
              )}
            />
            {hasAuth && (
              <Button css={IconButton.button} className="addRow" disableRipple onClick={btnAddRow}>
                {t('com.button.행추가', '행추가')}
              </Button>
            )}
            {hasAuth && (
              <Button css={IconButton.button} className="delRow" disableRipple onClick={btnDelRow}>
                {t('com.button.행삭제', '행삭제')}
              </Button>
            )}
            <Button
              css={IconButton.button}
              className="refresh"
              onClick={getCommonCode}
              disableRipple
            >
              {t('com.button.새로고침', '새로고침')}
            </Button>
            <Button
              css={IconButton.button}
              className="Exceldown"
              onClick={btnExcelExport}
              disableRipple
            >
              {t('com.button.다운로드', '화면 다운로드')}
            </Button>
          </ControlBtnGroup>
        )}
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={300}
        deferResizing={false}
        isFilter={true}
        beginningEdit={beginningEdit}
        initialized={onInitialized}
        cellEditEnded={cellEditEnded}
        align={'center'}
      />
      <GlobalBtnGroup>
        {hasAuth && (
          <Button css={IconButton.button} className="save" disableRipple onClick={btnSave}>
            {t('com.button.저장', '저장')}
          </Button>
        )}
      </GlobalBtnGroup>
    </>
  );
};

export default MpCodeLv3Grid;
